<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount } from "vue";
import Icon from "./Icon.vue";
import { useEscapeLayer } from "../composables/escape";
import { placeMenu } from "../composables/menuPlacement";
export interface MenuItem {
  label: string;
  icon?: string;
  danger?: boolean;
  disabled?: boolean;
  separator?: boolean;
  shortcut?: string;
  checked?: boolean;
  /** A square box at the end that shows `checked`, as WhatsApp's "Add to list" menu. */
  checkbox?: boolean;
  /** A number at the end, as a view's pill shows it. */
  count?: number;
  /** A colour dot before the label (a tag's colour); null is a tag without one. */
  swatch?: string | null;
  /** Icon colour, as in WhatsApp's attach menu. */
  tone?: string;
  action?: () => void;
}
export interface MenuBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
}
const props = defineProps<{
  open: boolean;
  x: number;
  /** Where the menu starts when it opens downward. */
  y: number;
  /** Where it ends when it opens upward for lack of room below; y by default. */
  above?: number | undefined;
  /** The area it stays in (a chat's messages, never over the composer); the window by default. */
  bounds?: MenuBounds | null | undefined;
  items: MenuItem[];
}>();
const emit = defineEmits<{ close: []; select: [item: MenuItem] }>();
const menu = ref<HTMLElement>(),
  extra = ref<HTMLElement>(),
  position = ref<Record<string, string>>({ left: "0px", top: "0px" }),
  extraPosition = ref<Record<string, string>>({ left: "0px", top: "0px" });
let previous: HTMLElement | null = null;
useEscapeLayer(() => props.open, close);
function close() {
  emit("close");
}
function outside(e: PointerEvent) {
  const target = e.target as Node;
  if (!menu.value?.contains(target) && !extra.value?.contains(target)) close();
}
/** Downward when it fits, upward when it does not, inside the bounds (see placeMenu). */
function place() {
  const m = menu.value;
  if (!m) return;
  const a = extra.value;
  const p = placeMenu({
    x: props.x,
    y: props.y,
    above: props.above,
    width: m.offsetWidth,
    height: m.offsetHeight,
    accessory: a ? { width: a.offsetWidth, height: a.offsetHeight } : null,
    area: props.bounds ?? {
      top: 0,
      bottom: innerHeight,
      left: 0,
      right: innerWidth,
    },
  });
  position.value = {
    left: p.menuLeft + "px",
    top: p.menuTop + "px",
    ...(p.maxHeight ? { maxHeight: p.maxHeight + "px" } : {}),
    visibility: "visible",
  };
  extraPosition.value = {
    left: p.accLeft + "px",
    top: p.accTop + "px",
    visibility: "visible",
  };
}
function key(e: KeyboardEvent) {
  if (!props.open) return;
  const items = [
    ...(menu.value?.querySelectorAll<HTMLButtonElement>(
      "button:not(:disabled)",
    ) || []),
  ];
  const i = items.indexOf(document.activeElement as HTMLButtonElement);
  if (["ArrowDown", "ArrowUp", "Home", "End", "Tab"].includes(e.key)) {
    e.preventDefault();
    e.stopPropagation();
    if (e.key === "Tab") {
      close();
      return;
    }
    items[
      e.key === "Home"
        ? 0
        : e.key === "End"
          ? items.length - 1
          : (i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length
    ]?.focus();
  }
}
watch(
  () => props.open,
  async (v) => {
    if (v) {
      previous = document.activeElement as HTMLElement;
      position.value = { left: "0px", top: "0px", visibility: "hidden" };
      extraPosition.value = { left: "0px", top: "0px", visibility: "hidden" };
      await nextTick();
      if (!props.open) return;
      place();
      await nextTick();
      menu.value
        ?.querySelector<HTMLButtonElement>("button:not(:disabled)")
        ?.focus();
      document.addEventListener("pointerdown", outside);
      document.addEventListener("keydown", key, true);
    } else {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", key, true);
      previous?.focus();
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", outside);
  document.removeEventListener("keydown", key, true);
});
</script>
<template>
  <Teleport to="body"
    ><div
      v-if="open && $slots.accessory"
      ref="extra"
      class="wa-menu-accessory"
      :style="extraPosition"
    >
      <slot name="accessory" />
    </div>
    <div
      v-if="open"
      ref="menu"
      class="wa-menu scroll"
      :style="position"
      role="menu"
    >
      <template v-for="(item, i) in items" :key="i"
        ><div v-if="item.separator" class="menu-separator" />
        <button
          v-else
          :role="item.checkbox ? 'menuitemcheckbox' : 'menuitem'"
          :aria-checked="item.checkbox ? !!item.checked : undefined"
          :class="{ danger: item.danger }"
          :disabled="item.disabled"
          @click="
            emit('select', item);
            close();
            item.action?.();
          "
        >
          <i
            v-if="item.swatch !== undefined"
            class="menu-swatch"
            :style="item.swatch ? { '--tag': item.swatch } : undefined"
          /><Icon
            v-if="item.icon"
            :name="item.icon"
            :size="19"
            :style="item.tone ? { color: item.tone } : undefined"
          /><span class="grow">{{ item.label }}</span
          ><span v-if="item.count" class="menu-count">{{ item.count }}</span
          ><span
            v-if="item.checkbox"
            class="menu-checkbox"
            :class="{ on: item.checked }"
            ><Icon
              v-if="item.checked"
              name="check"
              :size="14"
              :stroke="3" /></span
          ><Icon v-else-if="item.checked" name="check" :size="18" /><kbd
            v-if="item.shortcut"
            >{{ item.shortcut }}</kbd
          >
        </button></template
      >
    </div></Teleport
  >
</template>
